import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { adminApiError } from '../adminApi';
import { EvalBadge, EvalError } from '../evals/EvalBits';
import { shortId } from '../evals/format';
import {
  CALIBRATION_DROP_REASON_MAX_CHARS,
  CALIBRATION_MAX_SIZE,
  calibrationErrorText,
  exclusionList,
  exportFileName,
  itemHref,
  labelsHref,
  nextUnlabelled,
  reportHref,
  setHref,
  suiteKindText,
  versionRequest,
  type CalibrationItemSummary,
  type CalibrationSetDetail,
} from '../evals/labels';
import { ProgressBar } from '../evals/LabelItemView';
import { labelsApi } from '../evals/labelsApi';
import { GuideLink } from '../evals/GuideLink';
import { formatDate } from '../format';
import '../../styles/admin-eval-labels.css';

// One calibration set version (TOM-141; ADR 0234, ADR 0252): how it was drawn,
// its items in labelling order with this admin's progress, the export, and the
// next version, which adds newly drawn items or drops items, each with a
// reason. Never a verdict: the strata come summed per suite and kind until this
// admin has labelled every item, and no item links to its result or run.

/** Save the export as a file; a set holding test items needs the audited reveal. */
async function downloadExport(set: CalibrationSetDetail, revealTest: boolean): Promise<void> {
  const file = await labelsApi.exportSet(set.id, revealTest);
  const blob = new Blob([`${JSON.stringify(file, null, 2)}\n`], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = exportFileName(set);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ExportButton({ set }: { set: CalibrationSetDetail }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reveals = set.testItems > 0;

  async function run() {
    setBusy(true);
    setError(null);
    try {
      await downloadExport(set, reveals);
      setConfirming(false);
    } catch (err) {
      const body = adminApiError(err);
      setError(calibrationErrorText(body.error, body.message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="eval-label-export">
      {reveals && confirming ? (
        <span className="eval-callout" data-testid="eval-label-export-confirm">
          The export holds the full reply of {set.testItems} held-out test{' '}
          {set.testItems === 1 ? 'item' : 'items'}: exporting is an audited reveal.{' '}
          <button
            type="button"
            className="admin-button-secondary"
            disabled={busy}
            onClick={() => void run()}
            data-testid="eval-label-export-reveal"
          >
            {busy ? 'Exporting…' : 'Reveal and export'}
          </button>{' '}
          <button type="button" className="admin-link-button" onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </span>
      ) : (
        <button
          type="button"
          className="admin-button-secondary"
          disabled={busy}
          onClick={() => (reveals ? setConfirming(true) : void run())}
          data-testid="eval-label-export"
        >
          {busy ? 'Exporting…' : 'Export for a re-grade'}
        </button>
      )}
      {error !== null ? (
        <span className="eval-error-text" role="alert">
          {error}
        </span>
      ) : null}
    </span>
  );
}

function DrawInfo({ set }: { set: CalibrationSetDetail }) {
  const p = set.params;
  const exclusions = exclusionList(p.excluded);
  return (
    <section className="eval-card" data-testid="eval-label-draw-info">
      <h2 className="eval-label-h2">How this version was drawn</h2>
      <dl className="eval-kv">
        <dt>Seed</dt>
        <dd>
          <code>{set.seed}</code>
        </dd>
        <dt>Drawn</dt>
        <dd>
          {formatDate(set.createdAt)} by {set.createdBy ?? 'an admin no longer listed'}
        </dd>
        <dt>Judge version</dt>
        <dd>
          <code>{p.judgeVersion}</code>
        </dd>
        <dt>Items</dt>
        <dd>
          {set.version === 1
            ? `${p.added} drawn of ${p.size} asked`
            : `${p.copied} copied, ${p.added} newly drawn, ${p.dropped.length} dropped`}
          {set.testItems > 0 ? ` · ${set.testItems} from the test split` : ''}
        </dd>
        <dt>Runs</dt>
        <dd data-testid="eval-label-run-ids">
          {p.runIds === null
            ? 'every stored collect run'
            : p.runIds.map((id) => (
                <code key={id} title={id} className="eval-label-model">
                  {shortId(id)}
                </code>
              ))}
        </dd>
      </dl>
      {p.strata.length > 0 ? (
        <div className="scroll-x">
          <table
            className="admin-table eval-table eval-table--compact"
            data-testid="eval-label-strata"
          >
            <thead>
              <tr>
                <th>{p.blind ? 'Suite and kind' : 'Stratum'}</th>
                <th>Available</th>
                <th>Share</th>
                <th>Taken</th>
              </tr>
            </thead>
            <tbody>
              {p.strata.map((s) => (
                <tr key={s.key}>
                  <td>
                    <code>{s.key}</code>
                  </td>
                  <td>{s.available}</td>
                  <td>{s.target}</td>
                  <td>{s.taken}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {p.blind ? (
        <p className="admin-muted eval-sub">
          Verdict strata are summed per suite and kind until you have labelled every item: split by
          verdict they would name the judge&apos;s verdicts.
        </p>
      ) : null}
      {p.unfilled.length > 0 ? (
        <p className="eval-sub">
          Too few replies to fill:{' '}
          {p.unfilled.map((k) => (
            <code key={k} className="eval-label-model">
              {k}
            </code>
          ))}
        </p>
      ) : null}
      {exclusions.length > 0 ? (
        <details className="eval-drawer-details">
          <summary>Left out of the draw ({exclusions.reduce((n, e) => n + e.count, 0)})</summary>
          <ul className="eval-list">
            {exclusions.map((e) => (
              <li key={e.key}>
                {e.count} · {e.text}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {p.dropped.length > 0 ? (
        <>
          <h3>Dropped from the previous version</h3>
          <ul className="eval-list" data-testid="eval-label-dropped">
            {p.dropped.map((d) => (
              <li key={d.itemId}>
                <code>{d.caseId}</code>: {d.reason}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

function NewVersion({
  set,
  drops,
  onDropsChange,
}: {
  set: CalibrationSetDetail;
  drops: Map<string, string>;
  onDropsChange: (d: Map<string, string>) => void;
}) {
  const navigate = useNavigate();
  const [add, setAdd] = useState('0');
  const [seed, setSeed] = useState('');
  const [includeTest, setIncludeTest] = useState(set.includeTest);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const checked = versionRequest({ add, seed, includeTest, runIds: [], drops }, set.items);
    if (!checked.ok) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await labelsApi.createVersion(set.id, checked.body);
      onDropsChange(new Map());
      navigate(setHref(created.setId));
    } catch (err) {
      const body = adminApiError(err);
      setError(calibrationErrorText(body.error, body.message));
      setBusy(false);
    }
  }

  return (
    <form className="eval-card" onSubmit={submit} data-testid="eval-label-version">
      <h2 className="eval-label-h2">Make version {set.version + 1}</h2>
      <p className="admin-hint">
        A version never changes. The next one copies every item of this one, with its labels, except
        the items you drop, and adds newly drawn replies. Tick <strong>Drop</strong> on an item
        below to leave it out; each drop needs a reason, recorded with the version.
      </p>
      <div className="eval-filters">
        <label>
          Items to add
          <input
            type="text"
            inputMode="numeric"
            value={add}
            onChange={(e) => setAdd(e.target.value)}
            data-testid="eval-label-version-add"
          />
        </label>
        <label>
          Seed for the new draw (optional)
          <input
            type="text"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            maxLength={200}
          />
        </label>
      </div>
      <p className="admin-muted eval-sub">0 to {CALIBRATION_MAX_SIZE}.</p>
      <label className="eval-label-check">
        <input
          type="checkbox"
          checked={includeTest}
          onChange={(e) => setIncludeTest(e.target.checked)}
        />
        <span>Draw new items from the held-out test split too (audited when shown)</span>
      </label>
      {drops.size > 0 ? (
        <div className="eval-label-drops" data-testid="eval-label-drops">
          <h3>
            Dropping {drops.size} {drops.size === 1 ? 'item' : 'items'}
          </h3>
          {[...drops.entries()].map(([itemId, reason]) => (
            <label key={itemId} className="eval-label-note">
              Reason for item <code>{shortId(itemId)}</code>
              <textarea
                value={reason}
                rows={2}
                maxLength={CALIBRATION_DROP_REASON_MAX_CHARS}
                onChange={(e) => onDropsChange(new Map(drops).set(itemId, e.target.value))}
                placeholder="e.g. the case was found broken after the draw"
                data-testid={`eval-label-drop-reason-${itemId}`}
              />
            </label>
          ))}
        </div>
      ) : null}
      <div className="eval-label-save">
        <button
          type="submit"
          className="admin-button-affirm"
          disabled={busy}
          data-testid="eval-label-version-submit"
        >
          {busy ? 'Drawing…' : `Make version ${set.version + 1}`}
        </button>
      </div>
      {error !== null ? (
        <p className="admin-auth-banner error" role="alert" data-testid="eval-label-version-error">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function ItemsTable({
  set,
  items,
  drops,
  onDropsChange,
}: {
  set: CalibrationSetDetail;
  items: CalibrationItemSummary[];
  drops: Map<string, string>;
  onDropsChange: (d: Map<string, string>) => void;
}) {
  const latest = !set.superseded;
  const toggle = (id: string) => {
    const next = new Map(drops);
    if (next.has(id)) next.delete(id);
    else next.set(id, '');
    onDropsChange(next);
  };
  return (
    <div className="scroll-x">
      <table
        className="admin-table eval-table eval-label-stack-table"
        data-testid="eval-label-items"
      >
        <thead>
          <tr>
            <th>#</th>
            <th>Case</th>
            <th>Your label</th>
            <th>Labels</th>
            {latest ? <th>Drop</th> : null}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.itemId} data-testid={`eval-label-item-${item.itemId}`}>
              <td className="eval-label-cell-index">{i + 1}</td>
              <td className="eval-label-cell-main">
                <span className="eval-stack">
                  <span>
                    <Link to={itemHref(set.id, item.itemId)}>
                      <code>{item.caseId}</code>
                    </Link>
                    {item.testSplit ? (
                      <>
                        {' '}
                        <EvalBadge
                          tone="warn"
                          title="Held out (ADR 0238): shown only after an audited reveal"
                          testId="eval-label-test-badge"
                        >
                          Test split
                        </EvalBadge>
                      </>
                    ) : null}
                  </span>
                  <span className="admin-muted eval-sub">
                    {suiteKindText(item.suite, item.kindOrSurface)}
                  </span>
                </span>
              </td>
              <td data-label="You">
                {item.labelledByYou ? (
                  <EvalBadge tone="succeeded">Labelled</EvalBadge>
                ) : (
                  <span className="admin-muted">Not yet</span>
                )}
              </td>
              <td data-label="Labels">{item.labels}</td>
              {latest ? (
                <td data-label="Drop">
                  <input
                    type="checkbox"
                    checked={drops.has(item.itemId)}
                    onChange={() => toggle(item.itemId)}
                    aria-label={`Drop ${item.caseId} from the next version`}
                    data-testid={`eval-label-drop-${item.itemId}`}
                  />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminEvalLabelSet() {
  const { setId = '' } = useParams();
  const [set, setSet] = useState<CalibrationSetDetail | null>(null);
  const [items, setItems] = useState<CalibrationItemSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drops, setDrops] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let ignore = false;
    setSet(null);
    setItems(null);
    setError(null);
    setDrops(new Map());
    Promise.all([labelsApi.set(setId), labelsApi.items(setId)])
      .then(([s, i]) => {
        if (ignore) return;
        setSet(s);
        setItems(i);
      })
      .catch((err) => {
        const body = adminApiError(err);
        if (!ignore) setError(calibrationErrorText(body.error, body.message));
      });
    return () => {
      ignore = true;
    };
  }, [setId]);

  const next = items === null ? null : nextUnlabelled(items);
  const labelled = items?.filter((i) => i.labelledByYou).length ?? 0;

  return (
    <section className="admin-section eval-page">
      <p className="admin-hint">
        <Link to={labelsHref()}>← All calibration sets</Link>
      </p>
      <header className="admin-section-header">
        <h1>
          Calibration set <code title={set?.lineageId}>{set ? shortId(set.lineageId) : ''}</code>{' '}
          {set !== null ? `v${set.version}` : null}
        </h1>
        {set !== null ? (
          <span className="admin-count">
            {set.superseded ? (
              <EvalBadge tone="muted">Superseded</EvalBadge>
            ) : (
              <EvalBadge tone="succeeded">Latest</EvalBadge>
            )}
            {set.includeTest ? <EvalBadge tone="warn">Test split included</EvalBadge> : null}
          </span>
        ) : null}
        <GuideLink section="labels" />
      </header>

      <EvalError message={error} />
      {set === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {set !== null && items !== null ? (
        <>
          <div className="eval-label-summary" data-testid="eval-label-summary">
            <ProgressBar labelled={labelled} total={items.length} />
            <span className="eval-actions">
              {next !== null ? (
                <Link
                  className="admin-button-affirm eval-label-cta"
                  to={itemHref(set.id, next.itemId)}
                  data-testid="eval-label-start"
                >
                  {labelled === 0 ? 'Start labelling' : 'Continue labelling'} →
                </Link>
              ) : (
                <span className="eval-ok-text" data-testid="eval-label-all-done">
                  You have labelled every item.
                </span>
              )}
              <Link
                className="admin-button-secondary"
                to={reportHref(set.id)}
                data-testid="eval-label-report-link"
              >
                Agreement report
              </Link>
              <ExportButton set={set} />
            </span>
          </div>

          <ItemsTable set={set} items={items} drops={drops} onDropsChange={setDrops} />

          <div className="eval-cards eval-label-cards">
            <DrawInfo set={set} />
            {!set.superseded ? (
              <NewVersion set={set} drops={drops} onDropsChange={setDrops} />
            ) : (
              <p className="admin-hint">
                A later version of this set exists; a new version is made from the latest.
              </p>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
